import { useEffect, useState } from "react";
import ExpenseAttachments, {
    AttachmentStager, EMPTY_STAGED, EXPENSE_REQUIRED, commitStaged, stagedMissing,
} from "../components/ExpenseAttachments";
import { Plus, Eye } from "lucide-react";
import useServerTable from "../hooks/useServerTable";
import { useApi, useMutation } from "../hooks/useApi";
import { useAuth } from "../context/AuthContext";
import expenseAdvanceApi from "../api/expenseAdvanceApi";
import expenseMasterApi from "../api/expenseMasterApi";
import { employeeApi } from "../api/productionApi";
import { locationApi } from "../api/masterApi";
import Card from "../components/Card";
import Modal from "../components/Modal";
import Btn from "../components/Btn";
import Input from "../components/Input";
import Badge from "../components/Badge";
import Table from "../components/Table";
import Select2 from "../components/Select2";
import PageHeader from "../components/PageHeader";
import SearchBar from "../components/SearchBar";
import { EmployeeLedgerSection, PeriodReportSection, isoDate, thisWeekPeriod } from "../components/ExpenseAdvanceReports";
import T from "../theme/colors";

const money = v => `\u20B9${Number(v || 0).toLocaleString("en-IN", { minimumFractionDigits: 2 })}`;
// The LOCAL date. toISOString() is UTC, which in India is still yesterday
// until 5:30 in the morning - forms opened then defaulted to the wrong day.
const today = () => isoDate(new Date());

const EMPTY_ADVANCE = { employeeId: "", locationId: "", advanceDate: today(), amount: "", notes: "" };
const EMPTY_ENTRY = { expenseMasterId: "", paymentDate: today(), amount: "", paymentMode: "CASH", referenceNo: "", paidTo: "", narration: "" };

/**
 * Expense Advances — money given to an employee to run a location's
 * daily expenses ("₹25,000 to Shiv for Solapur").
 *
 * The register answers, per advance: given / spent / balance. View opens
 * the full record — every entry booked against the money — and is where
 * the employee records new spends: pick the category, the amount, done.
 * Each entry is a real Expense Payment linked back here, so the main
 * register and category reports see it too. Balance carries no floor —
 * overspend shows red, not zero.
 */
export default function ExpenseAdvancePage() {
    const { hasPermission } = useAuth();
    const canCreate = hasPermission("EXPENSE_ADVANCE", "CREATE");
    const canEdit = hasPermission("EXPENSE_ADVANCE", "EDIT");
    const canDelete = hasPermission("EXPENSE_ADVANCE", "DELETE");

    const { mutate } = useMutation();

    const { data: empData } = useApi(employeeApi.getActive, [], { cacheKey: "employeesActive" });
    const employees = Array.isArray(empData) ? empData : [];
    const { data: locData } = useApi(locationApi.getAll, [], { cacheKey: "locations" });
    const locations = Array.isArray(locData) ? locData : [];

    const [search, setSearch] = useState("");
    const [fEmp, setFEmp] = useState("");
    const [fLoc, setFLoc] = useState("");

    const t = useServerTable(expenseAdvanceApi.paged, {
        defaultSortBy: "advanceDate",
        defaultSortDir: "desc",
        defaultSize: 25,
        debounceMs: 300,
        extraFilters: {
            q: search.trim() || undefined,
            employeeId: fEmp || undefined,
            locationId: fLoc || undefined,
        },
    });

    const [modal, setModal] = useState(null);   // { mode: "create" } | { mode: "edit", row } | { mode: "view", row }
    // ADVANCES = the register | RECORD = employee-first spend entry |
    // LEDGER = the employee-wise given/spent/balance account |
    // REPORT = every holder's opening / given / spent / closing for a period.
    const [tab, setTab] = useState("ADVANCES");
    // Kept here, not in the tabs, so switching tabs does not lose them - and
    // so a row of the period report can open that employee's ledger for the
    // same period.
    const [ledgerEmployeeId, setLedgerEmployeeId] = useState("");
    const [ledgerPeriod, setLedgerPeriod] = useState({ from: "", to: "" });
    const [reportPeriod, setReportPeriod] = useState(thisWeekPeriod);

    const handleDelete = row => {
        if (!window.confirm(`Delete advance ${row.advanceNumber} (${row.employeeName})?`)) return;
        mutate(() => expenseAdvanceApi.delete(row.id), { onSuccess: t.reload, successMsg: "Deleted" });
    };

    const cols = [
        {
            key: "advanceNumber", label: "Advance No", sortKey: "advanceNumber",
            render: v => <span style={{ fontFamily: "monospace", fontSize: 12, fontWeight: 700, color: "var(--primary)" }}>{v}</span>,
        },
        {
            key: "advanceDate", label: "Date", sortKey: "advanceDate",
            render: v => <span style={{ whiteSpace: "nowrap" }}>{v}</span>,
        },
        {
            key: "employeeName", label: "Employee", sortKey: "employeeName",
            render: v => <span style={{ fontWeight: 600, color: "var(--text-0)" }}>{v}</span>,
        },
        { key: "locationName", label: "Location", sortKey: "locationName" },
        {
            key: "amount", label: "Given", sortKey: "amount",
            render: v => <span style={{ fontWeight: 600 }}>{money(v)}</span>,
        },
        {
            key: "spent", label: "Spent",
            render: v => <span style={{ color: "var(--text-1)" }}>{money(v)}</span>,
        },
        {
            key: "balance", label: "Balance",
            render: v => (
                <span style={{ fontWeight: 700, color: Number(v) < 0 ? "var(--danger)" : "var(--success)" }}>
                    {money(v)}
                </span>
            ),
        },
        {
            key: "notes", label: "Notes",
            render: v => v
                ? <span title={v} style={{
                    color: "var(--text-2)", display: "inline-block", maxWidth: 200,
                    overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", verticalAlign: "middle",
                }}>{v}</span>
                : <span style={{ color: "var(--text-2)" }}>—</span>,
        },
    ];

    return (
        <div className="fade-up" style={{ padding: "20px 24px" }}>
            <PageHeader
                title="Expense Advances"
                subtitle="Money given to employees to run location expenses — who holds what, what's spent, what's left."
                breadcrumb={["Finance", "Expense Advances"]}
                actions={canCreate && <Btn icon={<Plus size={14} />} onClick={() => setModal({ mode: "create" })}>Give Advance</Btn>}
            />

            <div style={{ display: "flex", gap: 10, marginBottom: 14, flexWrap: "wrap" }}>
                {[["ADVANCES", "Advances"], canCreate && ["RECORD", "Record Expense"], ["LEDGER", "Employee Ledger"], ["REPORT", "Period Report"]].filter(Boolean).map(([k, label]) => (
                    <button key={k} type="button" onClick={() => setTab(k)} style={{
                        padding: "9px 18px", borderRadius: 8, cursor: "pointer", fontSize: 14, fontWeight: 700,
                        border: `1px solid ${tab === k ? "var(--primary)" : T.border}`,
                        background: tab === k ? "var(--primary)" : "var(--bg-card)",
                        color: tab === k ? "#fff" : "var(--text-1)", transition: "all 0.15s",
                    }}>{label}</button>
                ))}
            </div>

            {tab === "RECORD" && canCreate && <RecordExpenseSection />}
            {tab === "LEDGER" && (
                <EmployeeLedgerSection
                    employeeId={ledgerEmployeeId}
                    onEmployeeChange={setLedgerEmployeeId}
                    period={ledgerPeriod}
                    onPeriodChange={setLedgerPeriod} />
            )}
            {tab === "REPORT" && (
                <PeriodReportSection
                    period={reportPeriod}
                    onPeriodChange={setReportPeriod}
                    onOpenEmployee={id => {
                        setLedgerEmployeeId(String(id));
                        setLedgerPeriod(reportPeriod);
                        setTab("LEDGER");
                    }} />
            )}

            {tab === "ADVANCES" && (
                <Card>
                    <div style={{
                        padding: "12px 16px", borderBottom: `1px solid ${T.border}`,
                        display: "flex", alignItems: "center", flexWrap: "wrap", gap: 10,
                    }}>
                        <SearchBar value={search} onChange={setSearch}
                            placeholder="Search advance no, employee, location, notes, amount…" />
                        <div style={{ minWidth: 170 }}>
                            <Select2 value={fEmp} onChange={setFEmp} placeholder="All employees"
                                options={[{ value: "", label: "All employees" },
                                ...employees.map(e => ({ value: String(e.id), label: e.employeeName }))]} />
                        </div>
                        <div style={{ minWidth: 150 }}>
                            <Select2 value={fLoc} onChange={setFLoc} placeholder="All locations"
                                options={[{ value: "", label: "All locations" },
                                ...locations.map(l => ({ value: String(l.id), label: l.locationName }))]} />
                        </div>
                    </div>

                    <Table
                        columns={cols}
                        serverMode t={t}
                        emptyText={search || fEmp || fLoc ? "No matches" : "No advances yet — click Give Advance"}
                        extraActions={row => (
                            <button type="button" title="View & record expenses" onClick={() => setModal({ mode: "view", row })}
                                style={{ background: "none", border: "none", cursor: "pointer", color: "var(--info)", padding: "5px 6px", display: "flex", alignItems: "center" }}>
                                <Eye size={14} />
                            </button>
                        )}
                        onEdit={canEdit ? (row => setModal({ mode: "edit", row })) : undefined}
                        onDelete={canDelete ? handleDelete : undefined}
                    />
                </Card>
            )}

            {(modal?.mode === "create" || modal?.mode === "edit") && (
                <AdvanceModal mode={modal.mode} row={modal.row}
                    employees={employees} locations={locations}
                    onClose={() => setModal(null)}
                    onSaved={() => { setModal(null); t.reload(); }} />
            )}
            {modal?.mode === "view" && (
                <ViewModal row={modal.row}
                    onClose={() => setModal(null)}
                    onChanged={t.reload} />
            )}
        </div>
    );
}

/* ── Create / edit the advance header ──────────────────────────────────── */

function AdvanceModal({ mode, row, employees, locations, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const { mutate } = useMutation();
    const [form, setForm] = useState(isEdit
        ? {
            employeeId: row.employeeId ? String(row.employeeId) : "",
            locationId: row.locationId ? String(row.locationId) : "",
            advanceDate: row.advanceDate || today(),
            amount: row.amount != null ? String(row.amount) : "",
            notes: row.notes || "",
        }
        : EMPTY_ADVANCE);
    const [errors, setErrors] = useState({});
    const f = key => val => setForm(p => ({ ...p, [key]: val }));

    const save = async () => {
        const errs = {};
        if (!form.employeeId) errs.employeeId = "Employee is required";
        if (!form.locationId) errs.locationId = "Location is required";
        if (!form.advanceDate) errs.advanceDate = "Date is required";
        if (!(Number(form.amount) > 0)) errs.amount = "Amount must be above zero";
        if (Object.keys(errs).length) { setErrors(errs); return; }
        const payload = {
            employeeId: Number(form.employeeId),
            locationId: Number(form.locationId),
            advanceDate: form.advanceDate,
            amount: Number(form.amount),
            notes: form.notes.trim() || null,
        };
        await mutate(
            () => isEdit ? expenseAdvanceApi.update(row.id, payload) : expenseAdvanceApi.create(payload),
            { onSuccess: onSaved, successMsg: isEdit ? "Advance updated" : "Advance created" }
        );
    };

    return (
        <Modal open onClose={onClose} width={460}
            title={isEdit ? `Edit ${row.advanceNumber}` : "Give Advance"}>
            <div style={{ display: "grid", gap: 12 }}>
                <Select2 label="Employee *" value={form.employeeId} onChange={f("employeeId")} placeholder="Who holds the money"
                    options={employees.map(e => ({ value: String(e.id), label: e.employeeName }))} error={errors.employeeId} />
                <Select2 label="Location *" value={form.locationId} onChange={f("locationId")} placeholder="Which location it runs"
                    options={locations.map(l => ({ value: String(l.id), label: l.locationName }))} error={errors.locationId} />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                    <Input label="Date *" type="date" value={form.advanceDate} onChange={f("advanceDate")} error={errors.advanceDate} />
                    <Input label="Amount ₹ *" type="number" value={form.amount} onChange={f("amount")} placeholder="25000" error={errors.amount} />
                </div>
                <Input label="Notes" value={form.notes} onChange={f("notes")} placeholder="Optional — e.g. September daily expenses" />
                <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 8 }}>
                    <Btn variant="outline" onClick={onClose}>Cancel</Btn>
                    <Btn onClick={save}>{isEdit ? "Update" : "Create"}</Btn>
                </div>
            </div>
        </Modal>
    );
}

/* ── View: the record + Add Expense ────────────────────────────────────── */

function ViewModal({ row, onClose, onChanged }) {
    const { hasPermission } = useAuth();
    const canEdit = hasPermission("EXPENSE_ADVANCE", "EDIT");
    const [head, setHead] = useState(row);        // refreshed after each entry
    const [entries, setEntries] = useState([]);
    const [loading, setLoading] = useState(true);

    const load = async () => {
        setLoading(true);
        try {
            const [h, e] = await Promise.all([
                expenseAdvanceApi.getById(row.id),
                expenseAdvanceApi.entries(row.id),
            ]);
            setHead(h.data?.data ?? h.data ?? row);
            const list = e.data?.data ?? e.data ?? [];
            setEntries(Array.isArray(list) ? list : []);
        } catch { /* toast comes from axios interceptor */ }
        setLoading(false);
    };
    useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [row.id]);

    const chip = (label, value, color) => (
        <div style={{ flex: 1, minWidth: 110, background: "var(--bg-input)", border: `1px solid ${T.border}`, borderRadius: 8, padding: "10px 12px" }}>
            <div style={{ fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.04em", color: T.text2 }}>{label}</div>
            <div style={{ fontSize: 17, fontWeight: 800, marginTop: 2, color: color || "var(--text-0)" }}>{money(value)}</div>
        </div>
    );

    return (
        <Modal open onClose={onClose} width={720}
            title={`${head.advanceNumber} — ${head.employeeName} (${head.locationName})`}>

            {/* Given / Spent / Balance */}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
                {chip("Given", head.amount)}
                {chip("Spent", head.spent)}
                {chip("Balance", head.balance, Number(head.balance) < 0 ? "var(--danger)" : "var(--success)")}
            </div>
            {head.notes && (
                <div style={{ fontSize: 12, color: T.text2, marginTop: 8 }}>{head.notes}</div>
            )}

            {/* Record of entries */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 16, marginBottom: 6 }}>
                <span style={{ fontSize: 11, fontWeight: 800, textTransform: "uppercase", letterSpacing: "0.03em", color: T.text2 }}>
                    Expenses recorded ({entries.length})
                </span>
                <span style={{ fontSize: 12, color: T.text2 }}>Recording moved to the Record Expense tab — pick the employee there.</span>
            </div>

            <div style={{ border: `1px solid ${T.border}`, borderRadius: 8, overflow: "hidden" }}>
                {loading && <div style={{ padding: 24, textAlign: "center", color: T.text2, fontSize: 13 }}>Loading…</div>}
                {!loading && entries.length === 0 && (
                    <div style={{ padding: 24, textAlign: "center", color: T.text2, fontSize: 13 }}>
                        Nothing recorded yet — record from the Record Expense tab
                    </div>
                )}
                {!loading && entries.length > 0 && (
                    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                        <thead>
                            <tr style={{ background: "var(--bg-input)" }}>
                                {["No", "Date", "Category", "Paid To", "Mode", "Amount", "Status"].map(h => (
                                    <th key={h} style={{
                                        padding: "8px 10px", fontSize: 11, fontWeight: 700, color: T.text2,
                                        textTransform: "uppercase", textAlign: h === "Amount" ? "right" : "left",
                                        borderBottom: `1px solid ${T.border}`, whiteSpace: "nowrap",
                                    }}>{h}</th>
                                ))}
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map(e => (
                                <tr key={e.id} style={{ borderBottom: `1px solid ${T.border}`, opacity: e.status === "CANCELLED" ? 0.55 : 1 }}>
                                    <td style={{ padding: "8px 10px", fontFamily: "monospace", fontSize: 11, color: "var(--primary)", whiteSpace: "nowrap" }}>{e.paymentNumber}</td>
                                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{e.paymentDate}</td>
                                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{e.expenseName}<ExpenseAttachments source="EXPENSE" entryId={e.id} attachments={e.attachments} canEdit={canEdit} title={`${e.paymentNumber} · ${e.expenseName || ""}`}
                                        required={e.status === "CANCELLED" ? [] : EXPENSE_REQUIRED} /></td>
                                    <td style={{ padding: "8px 10px", color: T.text1 }}>{e.paidTo || "—"}</td>
                                    <td style={{ padding: "8px 10px", color: T.text2, fontSize: 12 }}>{e.paymentMode}</td>
                                    <td style={{ padding: "8px 10px", textAlign: "right", fontWeight: 600, textDecoration: e.status === "CANCELLED" ? "line-through" : "none" }}>{money(e.amount)}</td>
                                    <td style={{ padding: "8px 10px" }}><Badge label={e.status} /></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 14 }}>
                <Btn variant="outline" onClick={onClose}>Close</Btn>
            </div>
        </Modal>
    );
}

/* ── Record Expense — employee-first ───────────────────────────────────── */

function RecordExpenseSection() {
    const { mutate } = useMutation();
    const { data: expData } = useApi(expenseMasterApi.getActive, [], { cacheKey: "expenseMastersActive" });
    const expenses = Array.isArray(expData) ? expData : [];
    // Only employees an advance was actually given to - there is nothing to
    // record or read against anyone else.
    const { data: holderData } = useApi(expenseAdvanceApi.holders, [], { cacheKey: "advanceHolders" });
    const employees = Array.isArray(holderData) ? holderData : [];

    const [employeeId, setEmployeeId] = useState("");
    const [ledger, setLedger] = useState(null);
    const [entry, setEntry] = useState(EMPTY_ENTRY);
    const [errors, setErrors] = useState({});
    // Bills and payment slips picked here — sent once the expense is recorded.
    const [staged, setStaged] = useState(EMPTY_STAGED);
    const f = key => val => setEntry(p => ({ ...p, [key]: val }));

    const loadBalance = (id) => {
        setLedger(null);
        if (!id) return;
        expenseAdvanceApi.employeeLedger(Number(id))
            .then(r => setLedger(r.data?.data ?? r.data ?? null))
            .catch(() => setLedger(null));
    };
    useEffect(() => { loadBalance(employeeId); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [employeeId]);

    const save = async () => {
        const errs = {};
        if (!employeeId) errs.employeeId = "Employee is required";
        if (!entry.expenseMasterId) errs.expenseMasterId = "Category is required";
        if (!entry.paymentDate) errs.paymentDate = "Date is required";
        if (!(Number(entry.amount) > 0)) errs.amount = "Amount must be above zero";
        // A bill AND a payment slip on every expense — cash too (a photo of the voucher).
        if (stagedMissing(EXPENSE_REQUIRED, [], staged).length) errs.attachments = true;
        if (Object.keys(errs).length) { setErrors(errs); return; }
        const saved = await mutate(
            () => expenseAdvanceApi.recordEmployeeExpense(Number(employeeId), {
                expenseMasterId: Number(entry.expenseMasterId),
                paymentDate: entry.paymentDate,
                amount: Number(entry.amount),
                paymentMode: entry.paymentMode,
                referenceNo: entry.referenceNo.trim() || null,
                paidTo: entry.paidTo.trim() || null,
                narration: entry.narration.trim() || null,
            }),
            { successMsg: "Expense recorded" }
        );
        if (!saved) return;   // the error is already shown
        const failed = saved.id ? await commitStaged("EXPENSE", saved.id, staged) : 0;
        if (failed > 0) {
            window.__garmentToast?.error?.(`Recorded, but ${failed} file${failed === 1 ? "" : "s"} did not upload — `
                + "add them from the entry's bills & slips.");
        }
        setEntry(p => ({ ...EMPTY_ENTRY, paymentDate: p.paymentDate, paymentMode: p.paymentMode }));
        setStaged(EMPTY_STAGED);
        setErrors({}); loadBalance(employeeId);
    };

    const bal = ledger ? Number(ledger.balance || 0) : null;

    return (
        <Card>
            <div style={{ padding: 16, maxWidth: 720 }}>
                <Select2 label="Employee *" value={employeeId} onChange={setEmployeeId}
                    placeholder="Who is spending the advance…"
                    options={employees.map(e => ({ value: String(e.id), label: e.employeeName }))}
                    error={errors.employeeId} />
                {employeeId && ledger && (
                    <div style={{ fontSize: 12, marginTop: 4, color: bal < 0 ? "var(--danger)" : T.text2 }}>
                        Advance balance: <b style={{ color: bal < 0 ? "var(--danger)" : "var(--success)" }}>{money(bal)}</b>
                        {"  "}· Given {money(ledger.totalGiven)} · Spent {money(ledger.totalSpent)}
                    </div>
                )}

                <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginTop: 12 }}>
                    <Select2 label="Expense Category *" value={entry.expenseMasterId} onChange={f("expenseMasterId")} placeholder="Diesel, Tea & Snacks…"
                        options={expenses.map(x => ({ value: String(x.id), label: x.expenseName }))} error={errors.expenseMasterId} />
                    <Input label="Date *" type="date" value={entry.paymentDate} onChange={f("paymentDate")} error={errors.paymentDate} />
                    <Input label="Amount ₹ *" type="number" value={entry.amount} onChange={f("amount")} placeholder="500" error={errors.amount} />
                </div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 10, marginTop: 10 }}>
                    <Select2 label="Mode *" value={entry.paymentMode} onChange={f("paymentMode")}
                        options={["CASH", "BANK", "UPI", "CHEQUE", "OTHER"].map(m => ({ value: m, label: m }))} />
                    <Input label="Paid To" value={entry.paidTo} onChange={f("paidTo")} placeholder="Vendor / shop" />
                    <Input label="Reference No" value={entry.referenceNo} onChange={f("referenceNo")} placeholder="Bill / UPI ref" />
                </div>
                <div style={{ marginTop: 10 }}>
                    <Input label="Narration" value={entry.narration} onChange={f("narration")} placeholder="What this was for" />
                </div>
                <div style={{ marginTop: 12 }}>
                    <AttachmentStager required={EXPENSE_REQUIRED} staged={staged} onChange={setStaged}
                        error={!!errors.attachments} />
                </div>
                <div style={{ fontSize: 12, color: T.text2, marginTop: 10 }}>
                    The spend books against this employee's oldest advance that still has money left — the
                    ledger tab shows the running account.
                </div>
                <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 12 }}>
                    <Btn onClick={save}>Record Expense</Btn>
                </div>
            </div>
        </Card>
    );
}
